import { useEffect, useRef, useState } from 'react';
import { logout as logoutRequest } from '../auth/api';
import { useSessionStore } from '../store/sessionStore';
import { pushNotification } from '../store/notificationStore';

interface UserMenuProps {
  onOpenProfile?: () => void;
}

export function UserMenu({ onOpenProfile }: UserMenuProps) {
  const user = useSessionStore((s) => s.user);
  const setAnonymous = useSessionStore((s) => s.setAnonymous);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onClickAway(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onClickAway);
    return () => window.removeEventListener('mousedown', onClickAway);
  }, [open]);

  async function handleLogout() {
    try {
      const logoutUrl = await logoutRequest();
      if (logoutUrl) {
        // 先跳 OAuth 清 SSO cookie，再让页面重新加载（此时本地 session 已清）
        const redirect = encodeURIComponent(window.location.origin);
        const sep = logoutUrl.includes('?') ? '&' : '?';
        window.location.href = `${logoutUrl}${sep}redirect=${redirect}`;
        return; // 跳转中，不要 setAnonymous 触发重渲染
      }
      // mock 模式没有 logout_url，直接清本地状态
      setAnonymous();
    } catch {
      pushNotification({ message: '退出失败，请稍后重试', level: 'danger', key: 'logout-error' });
    }
  }

  if (!user) return null;

  const initial = (user.display_name || user.username).slice(0, 1).toUpperCase();

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label="user-menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="os-user-menu-trigger"
      >
        {user.avatar_url ? (
          <img
            src={user.avatar_url}
            alt=""
            className="os-user-menu-avatar"
          />
        ) : (
          <span aria-hidden className="os-user-menu-avatar">
            {initial}
          </span>
        )}
        <span>{user.display_name || user.username}</span>
      </button>
      {open && (
        <div role="menu" className="os-user-menu">
          {user.must_change_password && (
            <div
              role="status"
              data-testid="must-change-password-banner"
              className="os-user-menu-banner"
            >
              当前仍在使用默认密码，建议尽快修改。
            </div>
          )}
          <div className="os-user-menu-meta">已登录为 {user.username}</div>
          {onOpenProfile && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onOpenProfile();
              }}
              className="os-user-menu-item"
            >
              修改资料
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void handleLogout();
            }}
            className="os-user-menu-item danger"
          >
            退出
          </button>
        </div>
      )}
    </div>
  );
}
